import { useMemo } from 'react';

import { Image } from '@codes/ui-kit/components/ui/image';
import { ListResult } from '@lemoncloud/codes-backend-api/dist/cores/types';
import { CloudStereo, CloudView } from '@lemoncloud/codes-goods-api';

import { AWSImage, Azure, GoogleCloudImage } from '@codes/assets';
import { Button, Skeleton } from '@codes/ui-kit';

interface CloudListProps {
    isFetchingClouds: boolean;
    isRefetchingClouds: boolean;
    cloudsFetchingError: any;
    clouds: ListResult<CloudView, any> | undefined;
    currentCloud: CloudView | undefined | null;
    setActiveCloudId: (id: string | null) => void;
    refetchClouds: () => void;
}

export const CloudList = ({
    isFetchingClouds,
    isRefetchingClouds,
    cloudsFetchingError,
    clouds,
    currentCloud,
    setActiveCloudId,
    refetchClouds,
}: CloudListProps) => {
    const getCloudImage = (stereo: CloudStereo | undefined) => {
        switch (stereo) {
            case 'aws':
                return AWSImage;
            case 'azure':
                return Azure;
            case 'gcp':
                return GoogleCloudImage;
            default:
                return AWSImage;
        }
    };

    const getPlanType = (planType: '' | 'free' | 'basic' | 'pro' | 'adv' | 'ent' | undefined) => {
        switch (planType) {
            case 'free':
            case 'basic':
                return 'Free';
            case 'pro':
                return 'Pro';
            case 'adv':
            case 'ent':
                return 'Advanced';
            default:
                return 'Free';
        }
    };

    return useMemo(() => {
        const isLoading = isFetchingClouds || isRefetchingClouds;

        if (isLoading) {
            return (
                <div className="flex flex-col gap-y-2">
                    <div className="flex gap-x-2 min-h-[40px]">
                        <Skeleton className="flex-1 " />
                        <Skeleton className="flex-1 " />
                        <Skeleton className="flex-1 " />
                    </div>
                    <Skeleton className="min-h-[200px]"></Skeleton>
                </div>
            );
        }

        if (cloudsFetchingError) {
            return (
                <div className="min-h-[90px] flex items-center justify-center flex-col gap-y-3 p-4 rounded-lg border border-red-200 bg-red-50">
                    <div className="text-center">
                        <div className="text-red-600 font-medium text-sm mb-1">Failed to load cloud information</div>
                        <div className="text-red-500 text-xs">Please check your network connection</div>
                    </div>
                    <Button
                        onClick={() => refetchClouds()}
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-100 hover:border-red-400"
                    >
                        Retry
                    </Button>
                </div>
            );
        }

        return (
            <div className="grid grid-cols-3 gap-[10px] mb-3">
                {clouds?.list.map(cloud => (
                    <button
                        key={cloud.id}
                        onClick={() => setActiveCloudId(cloud.id || null)}
                        className={`h-[43px] flex items-center justify-center gap-3 rounded-[10px] !border-[0.6px] bg-background transition-all duration-200 hover:border-gradient  ${
                            currentCloud?.id === cloud.id ? 'border-gradient ' : 'border-gray-300 '
                        } `}
                    >
                        <div className="w-7 flex items-center justify-center">
                            <Image src={getCloudImage(cloud.stereo)} className="w-full h-full object-contain" />
                        </div>
                        <span className="text-gray-900 font-medium text-[15px]">{getPlanType(cloud.planType)}</span>
                    </button>
                ))}
            </div>
        );
    }, [isFetchingClouds, isRefetchingClouds, clouds, cloudsFetchingError, currentCloud]);
};
